import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type PromotionTariffView } from '../lib/api';
import { useListingPromotion, usePromotionTariffs } from '../lib/queries';
import { haptic, isInTelegram, openInvoice } from '../lib/telegram';
import { Icon } from './Icon';
import { Button, EmptyState, ErrorState, Sheet, Spinner, errorMessage, formatDate, useToast } from './ui';

const ICON: Record<PromotionTariffView['type'], string> = { bump: 'arrowUp', vip: 'star', highlight: 'rocket' };

/**
 * Покупка продвижения своего объявления за Telegram Stars. Оплата — в окне Telegram (WebApp.openInvoice);
 * продвижение начисляет сервер после подтверждения оплаты от Telegram, приложение лишь ждёт результата.
 */
export function PromoteSheet({ open, onClose, listingId, listingTitle }: { open: boolean; onClose: () => void; listingId: string; listingTitle: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const tariffs = usePromotionTariffs(open);
  const state = useListingPromotion(listingId, open);
  const [busy, setBusy] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);

  const buy = async (tariff: PromotionTariffView) => {
    setBusy(tariff.code);
    try {
      const inv = await api<{ paymentId: string; invoiceUrl: string; stars: number }>(`/listings/${listingId}/promote`, {
        method: 'POST',
        json: { tariffCode: tariff.code },
      });
      const status = await openInvoice(inv.invoiceUrl);
      if (status === 'paid') {
        haptic.success();
        setWaiting(true);
        // Telegram подтверждает оплату боту, бот — серверу; обычно это занимает секунды.
        const applied = await waitApplied(inv.paymentId);
        setWaiting(false);
        void qc.invalidateQueries({ queryKey: ['listing-promotion', listingId] });
        void qc.invalidateQueries({ queryKey: ['listing', listingId] });
        void qc.invalidateQueries({ queryKey: ['my-listings'] });
        toast(applied ? t('promote.done') : t('promote.processing'), 'success');
        if (applied) onClose();
      } else if (status === 'failed') {
        toast(isInTelegram() ? t('promote.failed') : t('promote.onlyInTelegram'), 'error');
      }
    } catch (e) {
      haptic.error();
      toast(errorMessage(t, e), 'error');
    } finally {
      setBusy(null);
    }
  };

  const s = state.data;
  const until = (iso: string) => formatDate(iso);

  return (
    <Sheet open={open} onClose={onClose} title={t('promote.title')}>
      <p className="tiny muted promote-for">{listingTitle}</p>
      {s && (s.vipUntil || s.highlightedUntil) ? (
        <div className="notice notice-success">
          {s.vipUntil ? <div>{t('promote.vipActive', { date: until(s.vipUntil) })}</div> : null}
          {s.highlightedUntil ? <div>{t('promote.highlightActive', { date: until(s.highlightedUntil) })}</div> : null}
        </div>
      ) : null}
      {tariffs.isLoading ? (
        <Spinner />
      ) : tariffs.error ? (
        <ErrorState error={tariffs.error} onRetry={() => void tariffs.refetch()} />
      ) : !tariffs.data?.enabled ? (
        <EmptyState icon="rocket" title={t('promote.unavailable')} />
      ) : waiting ? (
        <Spinner label={t('promote.waiting')} />
      ) : (
        <ul className="tariffs">
          {tariffs.data.items.map((tr) => (
            <li key={tr.code} className="tariff">
              <span className="tariff-icon"><Icon name={ICON[tr.type]} size={22} /></span>
              <div className="tariff-body">
                <strong>{tr.name}</strong>
                <span className="tiny muted">
                  {tr.type === 'bump' ? t('promote.desc.bump') : tr.type === 'vip' ? t('promote.desc.vip', { days: tr.durationDays }) : t('promote.desc.highlight', { days: tr.durationDays })}
                </span>
              </div>
              <Button size="sm" loading={busy === tr.code} disabled={Boolean(busy)} onClick={() => void buy(tr)}>
                {tr.stars} ⭐
              </Button>
            </li>
          ))}
        </ul>
      )}
      <p className="tiny muted">{t('promote.note')}</p>
    </Sheet>
  );
}

async function waitApplied(paymentId: string): Promise<boolean> {
  for (let i = 0; i < 10; i++) {
    try {
      const r = await api<{ status: string; applied: boolean }>(`/payments/${paymentId}`);
      if (r.applied) return true;
      if (r.status === 'refunded' || r.status === 'failed') return false;
    } catch {
      /* повторим */
    }
    await new Promise((res) => setTimeout(res, 1500));
  }
  return false;
}
